import useOnClickOutside from '@/hooks/useOnClickOutside';
import classNames from 'classnames';
import React, { useRef, useState } from 'react';

type OptionsType = {
	id: string;
	label: React.ReactNode;
	onClick: () => void;
	disabled?: boolean;
};

export default function DropdownMenu({
	children,
	options,
	position = 'left',
	itemClassName,
	useFixedPosition
}: {
	children: React.ReactNode;
	options: OptionsType[];
	position?: 'left' | 'bottom';
	itemClassName?: string;
	useFixedPosition?: boolean;
}) {
	const [isOpen, setIsOpen] = useState(false);
	const menuRef = useRef<HTMLDivElement>(null);

	function handleOpen(e: React.MouseEvent) {
		e.preventDefault();
		e.stopPropagation();
		setIsOpen(!isOpen);
	}

	useOnClickOutside({
		ref: menuRef,
		handler: () => setIsOpen(false)
	});

	const divWidth = menuRef.current?.clientWidth ?? 0;
	const divHeight = menuRef.current?.clientHeight ?? 0;

	let panelStyle: React.CSSProperties = {
		top: position === 'left' ? '0' : divHeight + 3,
		right: position === 'left' ? divWidth + 3 : 0
	};

	let panelPositionClass = 'absolute z-10';

	if (useFixedPosition && isOpen && menuRef.current) {
		const rect = menuRef.current.getBoundingClientRect();
		panelPositionClass = 'fixed z-50';

		if (position === 'left') {
			panelStyle = {
				top: rect.top,
				right: window.innerWidth - rect.left + 3
			};
		} else if (position === 'bottom') {
			panelStyle = {
				top: rect.bottom + 3,
				left: rect.left,
				width: rect.width
			};
		}
	}

	return (
		<div
			ref={menuRef}
			className="relative z-50 flex h-full w-full justify-center"
		>
			<button
				type="button"
				onClick={handleOpen}
				className="flex h-full w-full items-center"
			>
				{children}
			</button>
			{isOpen && (
				<div
					style={panelStyle}
					className={`${panelPositionClass} overflow-hidden rounded-md border border-dark-grey bg-dm-black text-sm shadow-md ${position === 'left' ? 'min-w-min' : 'w-full'} `}
				>
					<div
						style={{ maxHeight: '300px' }}
						className="flex flex-col overflow-y-auto overflow-x-hidden"
					>
						{options.map((item, index) => (
							<DropdownMenuItem
								key={item.id + index}
								item={item}
								setIsOpen={setIsOpen}
								itemClassName={itemClassName}
							/>
						))}
					</div>
				</div>
			)}
		</div>
	);
}

function DropdownMenuItem({
	item,
	setIsOpen,
	itemClassName
}: {
	item: OptionsType;
	setIsOpen: (isOpen: boolean) => void;
	itemClassName?: string;
}) {
	function handleClick(e: React.MouseEvent) {
		e.preventDefault();
		e.stopPropagation();
		item.onClick();
		setIsOpen(false);
	}

	return (
		<button
			type="button"
			id="dropdown-menu-item"
			onClick={handleClick}
			disabled={item.disabled}
			className={classNames(
				itemClassName,
				item.disabled && 'cursor-not-allowed opacity-50',
				'flex w-full cursor-pointer whitespace-nowrap text-left'
			)}
		>
			<span className="overflow-hidden text-ellipsis">{item.label}</span>
		</button>
	);
}
